<template>
  <layout-private :customer="customer">
    <div class="row row-appt-flow">

      <div class="col-nav col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-md-10 offset-md-1">
        <hr class="hr-salmon">
        <!-- add loading indicator when no appointment or encounter -->
        <p v-if="!appointment || !encounter" class="snug loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
        <encounter-preview-box v-if="appointment && encounter" :appointment="appointment" :encounter="encounter" />
        <ul v-if="appointment && encounter" class="nav nav-tabs nav-appt-flow row">
          <!-- link to appointment setup view -->
           <li class="col-4 nav-item" :class="{'nav-item-setup': encounter && encounter.allSetupTasksCompleted }">
            <i v-if="encounter && encounter.allSetupTasksCompleted" class="fas fa-check"></i>
            <router-link 
              class="nav-link" 
              :class="{ active: $route.name === 'appointmentSetup' }" 
              :to="{ name: 'appointmentSetup'}"
            >
              <i class="fas fa-wrench"></i><br>
              <span>Appointment Setup</span>
            </router-link>
            </li>
          <!-- link to new treatment summary view  -->
          <li class="col-4 nav-item">
            <router-link 
              class="nav-link" 
              :class="{ active: $route.name === 'appointmentTreatSummary' }" 
              :to="{ name: 'appointmentTreatSummary'}"
            >
              <i class="fas fa-tooth"></i><br>
              <span>Treatment Summary</span>
            </router-link>
            </li>
          <!-- link to post appointment view -->
          <li class="col-4 nav-item"  :class="{'nav-item-setup': encounter && encounterPostAptCompleted }">
            <i v-if="encounter && encounterPostAptCompleted" class="fas fa-check"></i>
            <router-link 
              class="nav-link" 
              :class="{ active: $route.name === 'appointmentPost' }" 
              :to="{ name: 'appointmentPost'}"
            >
              <i class="fas fa-calendar-star"></i><br>
              <span>Scheduling &amp; Post-Apt</span>
            </router-link>
            </li>
        </ul>
      </div> <!-- /.col -->

      <div class="col-view col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-md-10 offset-md-1">

        <router-view v-if="appointment && encounter" v-on:showSlides="showSlidesModal=!showSlidesModal"></router-view>
        
        <hr class="hr-green">
        <button @click="clearSelectedEncounter" class="btn btn-link"><i class="fas fa-angle-left"></i> Back to Appointments</button>
      </div> <!-- /.col -->
      

      <encounter-type-slides-modal v-if="showSlidesModal && encounter" v-on:close="showSlidesModal=false" />
      <div v-if="showSlidesModal && encounter" class="modal-backdrop"></div>
    </div> <!-- /.row -->
  </layout-private>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import LayoutPrivate from '../components/LayoutPrivate.vue'
import ListErrors from '../components/ListErrors.vue'
import EncounterPreviewBox from '../components/EncounterPreviewBox.vue'
import EncounterTypeSlidesModal from '../components/EncounterTypeSlidesModal.vue'
import moment from 'moment-timezone';

export default {
  props: ['customer'],
  components: {
    LayoutPrivate, ListErrors, EncounterPreviewBox, EncounterTypeSlidesModal,
  },
  data() {
    return {
      patNum: null,
      errors: [],
      savedAppointmentEncounters: null,
      showSlidesModal: false,
    };
  },
  computed: {
    ...mapGetters(['user', 'user_props', 'custom_claims', 'appointment', 'encounter']),
    encounterPostAptCompleted() {
      if(this.encounter && Object.prototype.hasOwnProperty.call(this.encounter, 'pt_did_schedule') && this.encounter.pt_did_schedule !== null) {
        return true;
      }
      return false;
    },
    encounterAlreadySaved() {
      if(this.savedAppointmentEncounters && Object.keys(this.savedAppointmentEncounters).length && this.savedAppointmentEncounters[`${this.appointment.AptNum}_${this.aptDateStamp}_condensed`] && (!this.activeTreatmentPlan || this.activeTreatmentPlan.TreatPlanNum == this.savedAppointmentEncounters[`${this.appointment.AptNum}_${this.aptDateStamp}_condensed`].treat_plan_num)) {
        return true;
      }
      return false;
    },
    savedAptStatus() {
      if(this.encounterAlreadySaved) {
        return this.savedAppointmentEncounters[`${this.appointment.AptNum}_${this.aptDateStamp}_condensed`].encounter_setup_status;
      }
      return null;
    },
    aptDateStamp() {
      // store datestamp to use looking up encoutner in firestore
      if(this.appointment && this.appointment.AptDateTime) {
        return moment.tz(this.appointment.AptDateTime, 'America/Chicago').format("YYYYMMDD");
      }
      return null;
    },
  },
  watch: {
    // process active appointment
    appointment(newVal, oldVal) {
      console.log('appointment changed in AppointmentFlow.vue');
      if(newVal) {
        if(newVal.PatNum) {
          // set patnum when appointment changes
          this.patNum = newVal.PatNum;
        }
        // store patient name so don't have to load it again later during encounter
        if(newVal.PatientName) {
          this.$store.dispatch('updatePatName', newVal.PatientName);
        }
        // load all saved encounters for this AptNum
        this.loadAptEncounters();
      }
    },
    // process saved appointment encounters
    savedAppointmentEncounters(newVal, oldVal) {
      // console.log('savedAppointmentEncounters changed in AppointmentFlow.vue');
      if(newVal) {
        // if an encounter with 'condensed' type exists, load it into store
        if(newVal[`${this.appointment.AptNum}_${this.aptDateStamp}_condensed`]) {
          // console.log('saving condensed encounter into store');
          this.$store.dispatch('updateEncounter', newVal[`${this.appointment.AptNum}_${this.aptDateStamp}_condensed`]);
        }
      }
    },
  },
  methods: {
    clearSelectedEncounter() {
      this.$store.dispatch('clearEncounter');
      this.$store.dispatch('clearAppointment');
      this.$store.dispatch('clearPatNum');
      this.$store.dispatch('clearPatName');
      this.$store.dispatch('clearTreatmentPlan');
      this.$router.push({ name: 'appointmentSelect' });
      // this.$router.push({ name: 'appointmentEncounterType' });
    },
    loadAptEncounters() {
      // console.log('loadAptEncounters called');
      // exit if missing data
      if(!this.custom_claims || !this.custom_claims.customerId || !this.custom_claims.authorized_location) {
        // console.log('missing data to load appointment encounters');
        return;
      }
      Vue.firestore
        .collection(`customers/${this.custom_claims.customerId}/locations/${this.custom_claims.authorized_location}/encounters`)
        .where('appointment_num', '==', this.appointment.AptNum)
        .get()
        .then(docSnapshots => {
          if(docSnapshots.empty) {
            this.savedAppointmentEncounters = null;
            // console.log('no saved encounters found for this appointment, creating one');
            this.saveEncounterForSetup();
            return true;
          } else {
            // console.log('found saved encounters for this appointment');
            this.savedAppointmentEncounters = {};
            docSnapshots.forEach(docSnap => {
              this.savedAppointmentEncounters[docSnap.id] = docSnap.data();
            });
            return true;
          }
          
        })
        .catch(err => {
          console.log(err);
        });
    },
    async saveEncounterForSetup() {
      // exit if missing data
      if(!this.custom_claims || !this.custom_claims.customerId || !this.custom_claims.authorized_location || !this.appointment || !this.appointment.AptNum || !this.aptDateStamp) {
        console.log('missing data to save appointment encounters');
        return;
      }
      let encounterObject;
      // if already saved and options match, continue it
      if(this.encounterAlreadySaved) {
        return true;
        // console.log('encounter already saved, continuing it');
        // encounterObject = this.savedAppointmentEncounters[`${this.appointment.AptNum}_${this.aptDateStamp}_condensed`];
      } else {
        // console.log('saving new encounter for setup');
        // if not already saved, save it
        // create firestore object & key
        encounterObject = {
          encounter_id : `${this.appointment.AptNum}_${this.aptDateStamp}_condensed`,
          appointment_num: this.appointment.AptNum,
          encounter_setup_status: 'started',
          datestamp: this.aptDateStamp,
          timestamp: moment.tz(this.appointment.AptDateTime, 'America/Chicago').unix(),
          // TODO: ? save last updated timestamp - for follow-up tasks
          // TODO: ? save last updated datestamp - for follow-up tasks
          // pat_num: this.patNum, // do not store in databse unless absolutely necessary
        };
        const encounterId = `${this.appointment.AptNum}_${this.aptDateStamp}_condensed`;
        // save in firestore
          // save in /customers/{customerId}/locations/{locationId}/encounters/{encounterId}
        let encounterRef = Vue.firestore.doc(`/customers/${this.custom_claims.customerId}/locations/${this.custom_claims.authorized_location}/encounters/${encounterId}`);
        try {
          await encounterRef.set(encounterObject);
          // console.log('saved encounter');
        } catch(err) {
          console.error('Failed to save encounter for setup:', err);
          return;
        }
      }
      
      // save encounter data in store
      this.$store.dispatch('updateEncounter',encounterObject);
      // save TreatPlan in store
      this.$store.dispatch('updateTreatmentPlan', this.activeTreatmentPlan);
    },
  },
  mounted() {
    if(this.appointment) {
      if(this.appointment.PatNum) {
        this.patNum = this.appointment.PatNum;
      }
      // store patient name so don't have to load it again later during encounter
      if(this.appointment.PatientName) {
        this.$store.dispatch('updatePatName', this.appointment.PatientName);
      }
      // load all saved encounters for this AptNum
      this.loadAptEncounters();
    }
  },
  
};
</script>

<style lang="less" scoped>
@import "../assets/less/variables.less";

.row-appt-flow {
  padding-bottom: 30px;
}
.nav-appt-flow {
  border: none;
  margin-bottom: 30px;

  .nav-item {
    text-align: center;

    .fas {
      font-size: 16px;
      margin-bottom: 5px;
    }
  }
  .nav-item:not(.nav-item-setup) {
    padding-top: 21px;
  }
  .nav-link {
    border: 1px solid @dark-color;
    border-radius: 0;
    color: @dark-color;
    font-weight: bold;
    background-color: @light-color;
    padding: 20px 30px;
    text-align: center;
    text-decoration: none !important;
    
    .fas {
      margin-bottom: 5px;
      font-size: 20px;
    }
    &:hover {
      background-color: @color-green;
    }
  }
  .nav-link.active {
    background-color: @color-green;

    &:hover {
      background-color: @color-green;
      cursor: default;
    }
  }
}
.hr-green {
  margin: 30px 0;
}
</style>
